/**
 * Camera rig (S1-12/S1-13, S2-06): OrbitControls always orbit the origin, because the world is shifted so that the
 * focus point sits at the origin (floating origin). Focus changes fly along the smooth zoom-and-pan path of van Wijk
 * & Nuij (core/zoomPan.ts): out while travelling, so the target comes into view early, then in on it. Panning — and
 * zooming towards the cursor — is folded back into a pan offset, so the target stays at the origin.
 */
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { easeInOut } from '../core/scaleProfiles';
import { type Vec3, vec3 } from '../core/vec3';
import { DEFAULT_RHO, type ZoomPanPath, zoomPanPath } from '../core/zoomPan';

/** Flight duration bounds, s; the duration grows with the distance travelled (in log space). */
const FLY_MIN_S = 0.8;
const FLY_MAX_S = 2.0;
/** Seconds per e-fold of zoom along the path added to the minimum duration. */
const FLY_S_PER_EFOLD = 0.22;
/**
 * A flight advances by at most this much per frame, s: after a long frame (shader compile, GC) the camera continues
 * from where it was instead of jumping ahead (maintainer feedback: the intro zoom stuttered).
 */
const MAX_FLIGHT_STEP_S = 1 / 30;
/** Duration of a zoom step (double tap / two-finger tap), s. */
const ZOOM_STEP_S = 0.45;
/** Idle drift (S2-06): starts after this many seconds without input; OrbitControls autoRotate speed. */
const IDLE_S = 20;
const UP = new THREE.Vector3(0, 0, 1);
const DRIFT_SPEED = 0.12;

interface Flight {
  /** Offset from the new focus point at the start (display du), decays to zero. */
  from: Vec3;
  startDist: number;
  endDist: number;
  /** Focus flights: the zoom-and-pan path, in visible widths (du); `k` = visible width per camera distance. */
  path?: { p: ZoomPanPath; travel: number; k: number; scale: number };
  duration: number;
  elapsed: number;
  /** Intro sweep: azimuth (rad) still to turn back around the up axis, applied with the easing. */
  azimuth?: number;
  turned?: number;
}

export class CameraRig {
  readonly controls: OrbitControls;
  /** Float64 display position at the screen centre (the floating origin), du. */
  readonly origin = vec3();
  /** The camera never comes closer than this to the focus point (du); e.g. 1.2 × the focused planet's radius. */
  minFocusDistance = 0;
  private readonly pan = vec3();
  private flight: Flight | null = null;
  private idle = 0;
  /** Counts user inputs (and flights); cinematics yield to any input (S2-11). */
  inputCount = 0;
  private readonly rel = new THREE.Vector3();

  constructor(
    readonly camera: THREE.PerspectiveCamera,
    dom: HTMLElement,
  ) {
    camera.up.set(0, 0, 1); // ecliptic north is "up"
    this.controls = new OrbitControls(camera, dom);
    this.controls.target.set(0, 0, 0);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08; // a little more glide than the default (S2-06)
    this.controls.zoomSpeed = 1.2;
    this.controls.zoomToCursor = true;
    this.controls.rotateSpeed = 0.7;
    this.controls.autoRotateSpeed = DRIFT_SPEED;
    this.controls.screenSpacePanning = true;
    this.controls.minDistance = 1e-6;
    this.controls.maxDistance = 1e5;
    this.controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    this.controls.addEventListener('start', () => {
      this.noteInput();
      this.interruptFlight();
    });
  }

  get distance(): number {
    return this.camera.position.length();
  }

  setDistance(d: number): void {
    const len = this.camera.position.length();
    const clamped = Math.min(this.controls.maxDistance, Math.max(this.controls.minDistance, d));
    if (len > 0) this.camera.position.multiplyScalar(clamped / len);
    else this.camera.position.set(0, -clamped, 0);
  }

  /** Offset of the running flight applied in the last frame (du). */
  private readonly flightOffset = vec3();

  /**
   * The user takes over (drag, wheel, pinch): a focus flight stops where it is — otherwise it would set the distance
   * every frame and the wheel would do nothing until it ends (maintainer feedback: "zooming just stops"). The intro
   * sweep is not interrupted (it has its own skip).
   */
  private interruptFlight(): void {
    const f = this.flight;
    if (!f || f.azimuth !== undefined) return;
    this.pan.x += this.flightOffset.x;
    this.pan.y += this.flightOffset.y;
    this.pan.z += this.flightOffset.z;
    this.flightOffset.x = this.flightOffset.y = this.flightOffset.z = 0;
    this.flight = null;
  }

  /** Any user input: stops the idle drift and restarts its timer. */
  noteInput(): void {
    this.inputCount++;
    this.idle = 0;
    this.controls.autoRotate = false;
  }

  /** Starts a flight from the current origin to a new focus point. `instant` for reduced motion. */
  flyTo(newFocus: Vec3, endDist: number, instant: boolean): void {
    this.noteInput();
    this.pan.x = this.pan.y = this.pan.z = 0;
    if (instant) {
      this.flight = null;
      this.setDistance(endDist);
      return;
    }
    const from = vec3(this.origin.x - newFocus.x, this.origin.y - newFocus.y, this.origin.z - newFocus.z);
    const startDist = this.distance;
    const travel = Math.hypot(from.x, from.y, from.z);
    const k = 2 * Math.tan((this.camera.fov * Math.PI) / 360);
    const p = zoomPanPath(startDist * k, endDist * k, travel);
    const duration = Math.min(FLY_MAX_S, FLY_MIN_S + FLY_S_PER_EFOLD * DEFAULT_RHO * p.S);
    this.flight = { from, startDist, endDist, path: { p, travel, k, scale: 1 }, duration, elapsed: 0 };
  }

  /**
   * Animated zoom step by `factor` (< 1 = in). With `towards` (a display-space offset from the origin, du), the view
   * also moves towards that point like zoom-to-cursor.
   */
  zoomBy(factor: number, towards: Vec3 | null, instant: boolean): void {
    this.noteInput();
    const startDist = this.distance;
    const endDist = Math.min(this.controls.maxDistance, Math.max(this.minFocusDistance, startDist * factor));
    const shift = towards ? 1 - endDist / startDist : 0;
    const d = vec3((towards?.x ?? 0) * shift, (towards?.y ?? 0) * shift, (towards?.z ?? 0) * shift);
    this.pan.x += d.x;
    this.pan.y += d.y;
    this.pan.z += d.z;
    if (instant) {
      this.setDistance(endDist);
      return;
    }
    this.flight = { from: vec3(-d.x, -d.y, -d.z), startDist, endDist, duration: ZOOM_STEP_S, elapsed: 0 };
  }

  /** Eases the camera distance towards `target` (log space, time constant `tau` s) — cinematic framing. */
  easeDistance(target: number, dt: number, tau: number): void {
    if (this.flight || !(target > 0)) return;
    const cur = this.distance;
    const k = 1 - Math.exp(-Math.max(dt, 0) / tau);
    this.setDistance(Math.exp(Math.log(cur) + (Math.log(target) - Math.log(cur)) * k));
  }

  /**
   * Intro sweep (S2-12): starts `distFactor` times farther out and `azimuth` rad around the up axis, then glides to
   * the current view in `durationS`.
   */
  sweep(durationS: number, distFactor: number, azimuth: number, delayS = 0): void {
    const endDist = this.distance;
    this.camera.position.applyAxisAngle(UP, azimuth);
    this.setDistance(endDist * distFactor);
    this.flight = {
      from: vec3(),
      startDist: endDist * distFactor,
      endDist,
      duration: durationS,
      // a negative start holds the camera at the far start position for `delayS` (intro: while the scene warms up)
      elapsed: -delayS,
      azimuth,
      turned: 0,
    };
  }

  /** Starts a sweep that still waits at its start position (intro: the scene is warm earlier than the maximum hold). */
  releaseHold(): void {
    if (this.flight && this.flight.elapsed < 0) this.flight.elapsed = 0;
  }

  /** Ends any flight or sweep at its destination (intro skipped). */
  finishFlight(): void {
    const f = this.flight;
    if (!f) return;
    if (f.azimuth) this.camera.position.applyAxisAngle(UP, -(f.azimuth - (f.turned ?? 0)));
    this.setDistance(f.endDist);
    this.flight = null;
  }

  get flying(): boolean {
    return this.flight !== null;
  }

  /** Scales the camera distance (keeps apparent size during a scale-mode change). */
  scaleDistance(factor: number): void {
    if (!Number.isFinite(factor) || factor <= 0) return;
    if (this.flight) {
      this.flight.startDist *= factor;
      this.flight.endDist *= factor;
      if (this.flight.path) this.flight.path.scale *= factor;
    }
    const pos = this.camera.position;
    pos.multiplyScalar(factor);
    const len = pos.length();
    if (len < this.controls.minDistance) pos.multiplyScalar(this.controls.minDistance / len);
  }

  /**
   * Per-frame update; `focus` is the current display position of the focused object. `drift` allows the idle drift
   * (off with reduced motion).
   */
  update(dt: number, focus: Vec3, drift = true): void {
    this.idle += dt;
    this.controls.autoRotate = drift && !this.flight && this.idle > IDLE_S;
    this.controls.update(dt);
    // fold panning (and zoom-to-cursor) into the pan offset so the orbit target stays at the origin
    const tgt = this.controls.target;
    if (tgt.lengthSq() > 0) {
      this.pan.x += tgt.x;
      this.pan.y += tgt.y;
      this.pan.z += tgt.z;
      this.camera.position.sub(tgt);
      tgt.set(0, 0, 0);
    }
    let fx = 0,
      fy = 0,
      fz = 0;
    const f = this.flight;
    if (f) {
      f.elapsed += Math.min(dt, MAX_FLIGHT_STEP_S);
      const s = Math.min(1, Math.max(0, f.elapsed) / f.duration);
      const e = easeInOut(s);
      if (f.path) {
        const { p, travel, k, scale } = f.path;
        const sp = p.S * e;
        const left = travel > 0 ? 1 - p.u(sp) / travel : 0;
        fx = f.from.x * left;
        fy = f.from.y * left;
        fz = f.from.z * left;
        this.setDistance((p.w(sp) / k) * scale);
      } else {
        fx = f.from.x * (1 - e);
        fy = f.from.y * (1 - e);
        fz = f.from.z * (1 - e);
        this.setDistance(Math.exp(Math.log(f.startDist) + (Math.log(f.endDist) - Math.log(f.startDist)) * e));
      }
      if (f.azimuth) {
        const turn = f.azimuth * e - (f.turned ?? 0);
        this.camera.position.applyAxisAngle(UP, -turn);
        f.turned = f.azimuth * e;
      }
      if (s >= 1) this.flight = null;
    }
    this.flightOffset.x = fx;
    this.flightOffset.y = fy;
    this.flightOffset.z = fz;
    // keep out of the focused body: the camera's offset from the focus point is position + pan + flight offset
    if (this.minFocusDistance > 0) {
      const rel = this.rel.set(
        this.camera.position.x + this.pan.x + fx,
        this.camera.position.y + this.pan.y + fy,
        this.camera.position.z + this.pan.z + fz,
      );
      const len = rel.length();
      if (len < this.minFocusDistance && len > 0) {
        this.camera.position.addScaledVector(rel, this.minFocusDistance / len - 1);
      }
    }
    this.origin.x = focus.x + this.pan.x + fx;
    this.origin.y = focus.y + this.pan.y + fy;
    this.origin.z = focus.z + this.pan.z + fz;
  }

  dispose(): void {
    this.controls.dispose();
  }
}
